// ============================================================================
// OrgNodeCard — carte personne style Workday (E1c NAV v2, item 10)
// ============================================================================
// Carte blanche : initiales, nom (lien profil), poste, equipe, badge directeur.
// Clic carte = drill-in (recentre l'arbre) ; le nom reste un lien profil distinct
// (PDR-E1C-03). Bouton replier/deplier frere de la carte (pan-safe sur le canvas).

import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { GsTooltip } from '@gs/ui';
import type { OrgNode } from '../../lib/org/hierarchy';
import { getInitials } from '../../lib/profile/initials';
import { useTranslation } from '../../i18n';

/** Pastille d'initiales — remplace l'avatar image (decision gravee 2). */
function OrgInitials({ node, isRoot }: { node: OrgNode; isRoot: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-slate-100 font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200 ${
        isRoot ? 'h-12 w-12 text-field' : 'h-10 w-10 text-chip'
      } ${node.is_team_director || isRoot ? 'ring-2 ring-brand-500/40' : ''}`}
    >
      {getInitials(node.full_name, node.email)}
    </span>
  );
}

export function OrgNodeCard({
  node,
  depth,
  isCollapsed,
  isHighlighted,
  isFocused,
  onToggle,
  onDrillIn,
}: {
  node: OrgNode;
  depth: number;
  isCollapsed: boolean;
  isHighlighted?: boolean;
  isFocused?: boolean;
  onToggle: () => void;
  onDrillIn: () => void;
}) {
  const { t } = useTranslation();
  const isRoot = depth === 0;
  const displayName = node.full_name ?? node.email;

  const cardInner = (
    <>
      <OrgInitials node={node} isRoot={isRoot} />
      <GsTooltip content={displayName}>
        {node.href ? (
          <Link
            to={node.href}
            onClick={(e) => e.stopPropagation()}
            className={`block w-full truncate font-medium text-slate-900 underline-offset-2 hover:underline dark:text-slate-100 ${
              isRoot ? 'text-field' : 'text-chip'
            }`}
          >
            {displayName}
          </Link>
        ) : (
          <span
            tabIndex={0}
            className={`w-full truncate font-medium text-slate-900 dark:text-slate-100 ${
              isRoot ? 'text-field' : 'text-chip'
            }`}
          >
            {displayName}
          </span>
        )}
      </GsTooltip>
      {isRoot ? (
        <span aria-hidden="true" className="h-0.5 w-10 rounded-full bg-brand-500" />
      ) : null}
      {node.job_title ? (
        <GsTooltip content={node.job_title}>
          <span
            tabIndex={0}
            className="w-full truncate text-chip text-slate-500 dark:text-slate-400"
          >
            {node.job_title}
          </span>
        </GsTooltip>
      ) : null}
      {node.team_name ? (
        <span className="w-full truncate text-chip text-slate-400 dark:text-slate-500">
          {node.team_name}
        </span>
      ) : null}
      {!isRoot && node.is_team_director ? (
        <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-chip font-medium uppercase text-brand-700 dark:bg-brand-950/40 dark:text-brand-300">
          {t('gest:organigramme.node.director')}
        </span>
      ) : null}
    </>
  );

  return (
    <div
      id={`org-node-${node.email}`}
      className={`relative rounded-xl transition-shadow duration-200 ${
        isHighlighted || isFocused ? 'ring-2 ring-brand-500 ring-offset-2 ring-offset-transparent' : ''
      }`}
    >
      <button
        type="button"
        onClick={onDrillIn}
        aria-label={t('gest:organigramme.node.drillIn', { name: displayName })}
        className={`flex w-full touch-manipulation flex-col items-center gap-1.5 rounded-xl border bg-white p-3 text-center shadow-sm transition-colors duration-200 hover:border-slate-300 dark:bg-slate-800 dark:hover:border-slate-500 ${
          isRoot ? 'w-52' : 'w-44'
        } ${
          node.is_team_director && !isRoot
            ? 'border-brand-200 dark:border-brand-900'
            : 'border-slate-200 dark:border-slate-700'
        } ${isFocused ? 'border-brand-500 dark:border-brand-500' : ''}`}
      >
        {cardInner}
      </button>

      {node.descendant_count > 0 ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          aria-expanded={!isCollapsed}
          aria-label={
            isCollapsed
              ? t('gest:organigramme.node.expandBranch', {
                  name: displayName,
                  count: node.descendant_count,
                })
              : t('gest:organigramme.node.collapseBranch', { name: displayName })
          }
          className="absolute -bottom-3 left-1/2 z-10 inline-flex h-6 min-w-6 -translate-x-1/2 touch-manipulation items-center justify-center gap-0.5 rounded-full border border-slate-200 bg-white px-1.5 text-chip tabular-nums text-slate-500 shadow-sm transition-colors hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
        >
          {isCollapsed ? (
            <>
              <ChevronDown size={14} aria-hidden="true" />
              {node.descendant_count}
            </>
          ) : (
            <ChevronUp size={14} aria-hidden="true" />
          )}
        </button>
      ) : null}
    </div>
  );
}
